import { useState, useCallback } from 'react';
import type { UserGroup, Permission } from '../../../../../types/userRole';
import type { CreateGroupState } from '../types/createGroup';

interface UseCreateGroupProps {
  existingGroups: UserGroup[];
  onGroupCreated: (newGroup: UserGroup) => void;
  onConfigurePermissions?: (group: UserGroup) => void;
  onCreateGroupPermissions?: (group: UserGroup) => void;
  onSuccessModalComplete?: () => void;
}

export const useCreateGroup = ({ existingGroups, onGroupCreated, onConfigurePermissions, onCreateGroupPermissions, onSuccessModalComplete }: UseCreateGroupProps) => {
  const [state, setState] = useState<CreateGroupState>({
    currentStep: 'create',
    newGroup: {
      name: '',
    },
    isCreating: false,
    hasPermissions: false,
    showModals: {
      create: false,
      configure: false,
      permissions: false,
      success: false,
    },
  });

  // Get existing group names for validation
  const existingGroupNames = existingGroups.map(group => group.name.toLowerCase());

  // Generate unique group ID
  const generateGroupId = useCallback((): number => {
    const maxId = existingGroups.reduce((max, group) => Math.max(max, group.id), 0);
    return maxId + 1;
  }, [existingGroups]);

  // Start the create group flow
  const startCreateGroup = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: 'create',
      showModals: {
        ...prev.showModals,
        create: true,
      },
    }));
  }, []);

  // Handle group name submission
  const handleCreateGroup = useCallback((groupName: string) => {
    const newId = generateGroupId();
    const newGroup = {
      name: groupName,
      id: newId,
      groupId: `Group ID: ${newId}`,
    };

    setState(prev => ({
      ...prev,
      currentStep: 'configure',
      newGroup,
      showModals: {
        ...prev.showModals,
        create: false,
        configure: true,
      },
    }));
  }, [generateGroupId]);

  // Handle skip permissions configuration
  const handleSkipPermissions = useCallback(() => {
    if (!state.newGroup.id) return;

    const finalGroup: UserGroup = {
      id: state.newGroup.id,
      name: state.newGroup.name,
      groupId: state.newGroup.groupId || `Group ID: ${state.newGroup.id}`,
      memberCount: 0,
      permissions: [],
      selected: false,
    };

    onGroupCreated(finalGroup);

    setState(prev => ({
      ...prev,
      currentStep: 'success',
      hasPermissions: false,
      showModals: {
        ...prev.showModals,
        configure: false,
        success: true,
      },
    }));
  }, [state.newGroup, onGroupCreated]);

  // Handle configure permissions
  const handleConfigurePermissions = useCallback(() => {
    if (state.newGroup.id) {
      const tempGroup: UserGroup = {
        id: state.newGroup.id,
        name: state.newGroup.name,
        groupId: state.newGroup.groupId || `Group ID: ${state.newGroup.id}`,
        memberCount: 0,
        permissions: [],
        selected: false,
      };
      
      setState(prev => ({
        ...prev,
        showModals: {
          ...prev.showModals,
          configure: false,
        },
      }));
      
      // Use create-specific callback for new groups
      if (onCreateGroupPermissions) {
        onCreateGroupPermissions(tempGroup);
      } else if (onConfigurePermissions) {
        onConfigurePermissions(tempGroup);
      } else {
        // Fallback to internal permissions modal
        setState(prev => ({
          ...prev,
          currentStep: 'permissions',
          showModals: {
            ...prev.showModals,
            configure: false,
            permissions: true,
          },
        }));
      }
    }
  }, [onConfigurePermissions, onCreateGroupPermissions, state.newGroup]);

  // Handle permissions configuration complete
  const handlePermissionsComplete = useCallback((permissions: Permission[] = []) => {
    if (!state.newGroup.id) return;

    const finalGroup: UserGroup = {
      id: state.newGroup.id,
      name: state.newGroup.name,
      groupId: state.newGroup.groupId || `Group ID: ${state.newGroup.id}`,
      memberCount: 0,
      permissions,
      selected: false,
    };

    onGroupCreated(finalGroup);

    setState(prev => ({
      ...prev,
      currentStep: 'success',
      hasPermissions: permissions.length > 0,
      showModals: {
        ...prev.showModals,
        permissions: false,
        success: true,
      },
    }));
  }, [state.newGroup, onGroupCreated]);

  // Handle permissions completion from external component (Dashboard)
  const handleExternalPermissionsComplete = useCallback((permissions: Permission[] = []) => {
    console.log('useCreateGroup: handleExternalPermissionsComplete called', {
      hasNewGroupId: !!state.newGroup.id,
      newGroupName: state.newGroup.name,
      permissionsCount: permissions.length,
      currentStep: state.currentStep
    });
    
    if (!state.newGroup.id) {
      console.log('useCreateGroup: No newGroup.id, returning early');
      return;
    }

    const finalGroup: UserGroup = {
      id: state.newGroup.id,
      name: state.newGroup.name,
      groupId: state.newGroup.groupId || `Group ID: ${state.newGroup.id}`,
      memberCount: 0,
      permissions,
      selected: false,
    };

    console.log('useCreateGroup: Adding group to table:', finalGroup);
    onGroupCreated(finalGroup);

    console.log('useCreateGroup: Setting success modal to show');
    setState(prev => ({
      ...prev,
      currentStep: 'success',
      hasPermissions: permissions.length > 0,
      showModals: {
        ...prev.showModals,
        configure: false,
        permissions: false,
        success: true, // 🔥 This will now be visible since we're back in UserRoleManagement
      },
    }));
  }, [state.newGroup, onGroupCreated]);

  // Handle success completion (auto-redirect)
  const handleSuccessComplete = useCallback(() => {
    console.log('Success modal completing...');
    
    // Reset state first
    setState(prev => ({
      ...prev,
      currentStep: 'create',
      newGroup: {
        name: '',
      },
      hasPermissions: false,
      showModals: {
        create: false,
        configure: false,
        permissions: false,
        success: false,
      },
    }));
    
    // Call the external handler to navigate back to group management
    if (onSuccessModalComplete) {
      console.log('Calling onSuccessModalComplete...');
      // Use setTimeout to ensure state update completes first
      setTimeout(() => {
        onSuccessModalComplete();
      }, 100);
    }
  }, [onSuccessModalComplete]);

  // Close all modals
  const closeAllModals = useCallback(() => {
    setState(prev => ({
      ...prev,
      showModals: {
        create: false,
        configure: false,
        permissions: false,
        success: false,
      },
    }));
  }, []);

  // Close specific modal
  const closeModal = useCallback((modalType: keyof CreateGroupState['showModals']) => {
    setState(prev => ({
      ...prev,
      showModals: {
        ...prev.showModals,
        [modalType]: false,
      },
    }));
  }, []);

  return {
    state,
    existingGroupNames,
    actions: {
      startCreateGroup,
      handleCreateGroup,
      handleSkipPermissions,
      handleConfigurePermissions,
      handlePermissionsComplete,
      handleExternalPermissionsComplete,
      handleSuccessComplete,
      closeAllModals,
      closeModal,
    },
  };
};